import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import TestCase from '../../models/customAssistant/TestCase';
import ModalLayout from '../structure/ModalLayout';
import ModalHeader from '../general/ModalHeader';
import FooterBar from '../structure/FooterBar';
import CustomScrollbars from '../structure/CustomScrollbars';
import Button from '../general/Button';
import TestCaseContainer from '../../hooks/TestCaseContainer';
import TextField from './TextField';
import { JsonEditor as Editor } from 'jsoneditor-react';
import {
    color_shades_dark, color_shades_darker, color_shades_darkest, color_shades_lighter,
    color_variants_green_2, color_variants_red_2, color_defaults_black
} from '../../constants/colors';
import useWindowSize from '../../hooks/UseWindowSize';
import Scrollbars from 'react-custom-scrollbars';
import set from 'lodash';
import ConversationSessionState from '../../models/sessionAttributes/ConversationSessionState';
import UserProfile from '../../models/userProfile/UserProfile';
import CheckboxField from './CheckboxField';
import TestCaseHistoryViewer from './TestCaseHistoryViewer';

interface TestCaseEditorProps {
    initialValue: TestCase;
    onSave: (updated: TestCase) => void;
    onClose: () => void;
    isContentItemView: boolean
    contentItemId?: string
}

type AssertionVariant = 'default' | 'success' | 'failure';




interface Turn {
    user: string;
    assistant: string;
}

const TestCaseEditingModal: React.FC<TestCaseEditorProps> = ({
    initialValue,
    onSave,
    onClose,
    isContentItemView,

}) => {
    const [testCase, setTestCase] = useState(JSON.parse(JSON.stringify(initialValue)));
    const [fallbackId, setFallbackId] = useState(null);
    const [assistantResponse, setAssistantResponse] = useState("");
    const [turns, setTurns] = useState<Turn[]>([]);
    const [isContentItemTestCase, setIsContentItemTestCase] = useState(testCase.contentItemId?.length > 0)

    const testCaseContainer = TestCaseContainer.useContainer();
    const windowSize = useWindowSize();
    const MODAL_SCROLLBAR_SUBTRACTION: number = 185;
    const subScrollbarRef = useRef(null);
    useEffect(() => {
        if (subScrollbarRef?.current) {
            subScrollbarRef?.current.scrollToBottom();
        }
    }, [turns]);

    useEffect(() => {
        if (testCaseContainer?.fullResponses[testCase.name]?.outputSpeech?.length) {
            setAssistantResponse(testCaseContainer?.fullResponses[testCase.name]?.outputSpeech);
            if (subScrollbarRef?.current) {
                subScrollbarRef?.current.scrollToBottom();

            }
        }
    }, [testCaseContainer?.fullResponses]);

    useEffect(() => {
        let tc = testCase;
        if (!testCase)
            tc = initialValue
        var turnHistoryState = tc.sessionState.sessionAttributes["ConversationTurnHistoryState"];
        if (!turnHistoryState) {
            turnHistoryState = tc.sessionState.sessionAttributes["conversationTurnHistoryState"];
        }
        if (turnHistoryState?.TurnHistory?.length) {
            const newTurns = [];
            for (const turn of turnHistoryState.TurnHistory) {
                newTurns.push({
                    user: turn.UserInput,
                    assistant: turn.AssistantResponse
                });
            }
            setTurns(newTurns);
            if (subScrollbarRef?.current) {
                subScrollbarRef?.current.scrollToBottom();
            }
        }

    }, [initialValue, testCase])

    const windowHeight = (windowSize.windowSize.innerHeight * .9) - MODAL_SCROLLBAR_SUBTRACTION;

    const CustomEditorWrapper = styled.div`
        height: ${windowHeight};
    `;

    const importTestCaseFromClipboard = async () => {
        const clipboardText = await navigator.clipboard.readText();
        try {
            const parsedTestCase = JSON.parse(clipboardText) as TestCase;
            if (parsedTestCase.sessionState) {
                setTestCase(parsedTestCase);
            }
        } catch (error) {
            console.error("Failed to parse clipboard text as TestCase:", error);
        }
    }

    const copyTestCaseToClipboard = async () => {
        const testCaseToCopy = { ...testCase };
        testCaseToCopy.name = `${testCaseToCopy.name}-copied`;
        const testCaseString = JSON.stringify(testCaseToCopy, null, 2);
        await navigator.clipboard.writeText(testCaseString);
    }

    const handleChange = <K extends keyof TestCase>(
        field: K,
        value: TestCase[K],
    ) => {
        if (field === "sessionState" || field === "userProfile") {
            setTestCase(prev => ({ ...prev, [field]: JSON.parse(value as string) }));

        } else {
            setTestCase(prev => ({ ...prev, [field]: value }));
        }
    };

    const setTestCaseContentItem = () => {
        const updatedTestCase = testCase;
        const sessionAttributes = updatedTestCase?.sessionState?.sessionAttributes
        const sessionHistory = "conversationTurnHistoryState" in sessionAttributes ? sessionAttributes["conversationTurnHistoryState"] : null
        const lastTurn = sessionHistory !== null ? sessionHistory["TurnHistory"].at(-1) : null;
        const contentId = lastTurn !== null && "ContentId" in lastTurn ? lastTurn["ContentId"] : null
        const currentFallbackId = lastTurn !== null && "FallbackMessageId" in lastTurn ? lastTurn["FallbackMessageId"] : null

        if (isContentItemTestCase) {
            updatedTestCase.contentItemId = null;
            setIsContentItemTestCase(false)
            setFallbackId(null);
        }
        else {
            updatedTestCase.contentItemId = contentId;
            setFallbackId(currentFallbackId);
            setIsContentItemTestCase(true)
        }
        setTestCase(updatedTestCase);
    }

    const handleClose = () => {
        if (viewResponseJson || editSessionState || editUserProfile) {
            setViewResponseJson(false);
            setEditSessionState(false);
            setEditUserProfile(false);
        } else {
            onClose();
        }
    }

    const simulate = async () => {
        await testCaseContainer.simulateTestCase(testCase);
    };

    const updateAssertion = (idx: number, value: string) => {
        const next = [...(testCase.assertions ?? [])];
        next[idx] = value;
        handleChange('assertions', next);
    };

    const addAssertion = () =>
        handleChange('assertions', [...(testCase.assertions ?? []), '']);

    const removeAssertion = (idx: number) => {
        let next = [...(testCase.assertions ?? [])];
        next.splice(idx, 1);
        if (testCase.assertions?.length === 1 || testCase.assertions?.length === 0) {
            next = [];
        }
        handleChange('assertions', next);
    };

    const handleSave = () => {
        if (testCase.name?.length > 0) {
            onSave({ ...testCase, })
            if (fallbackId !== null) {
                const testCaseCopy = { ...testCase }
                testCaseCopy.name = `${testCase.name}-fallback`
                testCaseCopy.contentItemId = fallbackId
                onSave({ ...testCaseCopy, })
            }
            onClose();
        }
        else {
            // Todo: make a popup telling the user to name the test
        }
    };

    const sessionStateEditorRef = useRef(null);
    const userStateEditorReft = useRef(null)
    const responseJsonEditorRef = useRef(null)

    useEffect(() => {
        if (sessionStateEditorRef.current !== null) {
            sessionStateEditorRef.current.set(testCase.sessionState ? testCase.sessionState : {});
        }
    }, [testCase.sessionState])

    useEffect(() => {
        if (userStateEditorReft.current !== null) {
            userStateEditorReft.current.set(testCase.userProfile ? testCase.userProfile : {});
        }
    }, [testCase.userProfile])

    const setSessionRef = instance => {
        if (instance) {
            sessionStateEditorRef.current = instance.jsonEditor;
        } else {
            sessionStateEditorRef.current = null;
        }
    };

    const setUserRef = instance => {
        if (instance) {
            userStateEditorReft.current = instance.jsonEditor;
        } else {
            userStateEditorReft.current = null;
        }
    };

    const setResponseJsonRef = instance => {
        if (instance) {
            responseJsonEditorRef.current = instance.jsonEditor;
        } else {
            responseJsonEditorRef.current = null;
        }
    };

    const getAssertionText = (assertion: string) => {
        const keys = Object.keys(testCaseContainer?.fullResponses);
        if (!assertion?.length || !keys?.length || !testCase?.name?.length || !keys.includes(testCase?.name)) return "?";
        if (testCaseContainer?.fullResponses[testCase?.name]) {
            const asserts = testCaseContainer?.fullResponses[testCase?.name]?.assertions;
            if (!asserts) return "?";
            const assertionKeys = Object.keys(asserts);
            if (!assertionKeys?.length) return "?";
            if (!assertionKeys.includes(assertion)) return "?";
            return testCaseContainer?.fullResponses[testCase?.name].assertions[assertion];
        }
        return "?";
    }

    const [editSessionState, setEditSessionState] = useState(false);
    const [editUserProfile, setEditUserProfile] = useState(false);
    const [viewResponseJson, setViewResponseJson] = useState(false);

    const renderSession = () => {
        return (<CustomEditorWrapper>
            <CustomEditor>
                <Editor
                    ref={setSessionRef}
                    value={testCase.sessionState}
                    onChange={value => null}
                    enableTransform={false}
                    enableSort={false}
                    mode="tree"
                    allowedModes={['tree', 'code']}
                    sortObjectKeys={true}
                />
            </CustomEditor>
        </CustomEditorWrapper>)
    }
    const renderUserProfile = () => {
        return (<CustomEditorWrapper>
            <CustomEditor>
                <Editor
                    ref={setUserRef}
                    value={testCase.userProfile}
                    onChange={value => null}
                    enableTransform={false}
                    enableSort={false}
                    mode="tree"
                    allowedModes={['tree', 'code']}
                    sortObjectKeys={true}
                />
            </CustomEditor>
        </CustomEditorWrapper >)
    }
    const renderResponseJson = () => {
        return (<CustomEditorWrapper>
            <CustomEditor>
                <Editor
                    ref={setResponseJsonRef}
                    value={testCaseContainer?.fullResponses[testCase?.name]}
                    onChange={() => { }}
                    enableTransform={false}
                    enableSort={false}
                    mode="tree"
                    allowedModes={['tree', 'code']}
                    sortObjectKeys={true}
                />
            </CustomEditor>
        </CustomEditorWrapper >)
    }
    return (
        <ModalLayout isVisible={true}>
            <ModalHeader title={testCase.name?.length > 0 ? `Edit ${testCase.name}` : "Create new Test Case"} onClose={() => handleClose()} />
            {!(editSessionState || editUserProfile || viewResponseJson) ? (
                <CustomScrollbars
                    autoHide
                    autoHeight
                    autoHeightMax={(windowSize.windowSize.innerHeight * .9) - MODAL_SCROLLBAR_SUBTRACTION}
                >
                    <Body minHeight={(windowSize.windowSize.innerHeight * .9) - MODAL_SCROLLBAR_SUBTRACTION}>

                        <HistoryViewerButtons>
                            <HistoryButton type="button" themes={['secondary-small', 'end']} text="Session State" onClick={() => setEditSessionState(true)} />
                            <HistoryButton type="button" themes={['secondary-small', 'end']} text="User Profile" onClick={() => setEditUserProfile(true)} />
                        </HistoryViewerButtons>
                        <Center>
                            <SectionCard>
                                <TopRow>
                                    <ThinTextField
                                        useForm={false}
                                        label="Name of test case"
                                        id="test-case-name"
                                        type="text"
                                        value={testCase.name}
                                        placeholder="Enter a name for your test case… (Required)"
                                        onChange={e => handleChange('name', e.target.value)}
                                        autoFocus
                                    />
                                    <TopRowButton
                                        type="button"
                                        themes={['start', 'secondary-small']}
                                        text="Copy to Clipboard"
                                        onClick={() => copyTestCaseToClipboard()}
                                    />
                                    <TopRowButton
                                        type="button"
                                        themes={['start', 'secondary-small']}
                                        text="Paste from Clipboard"
                                        onClick={() => importTestCaseFromClipboard()}
                                    />
                                    {!isContentItemView && <DetailedSimpleCheckbox
                                        id={"simpleView"}
                                        label={"Content Item Test"}
                                        checked={isContentItemTestCase}
                                        onChange={() => setTestCaseContentItem()}
                                    />}
                                </TopRow>
                            </SectionCard>
                        </Center>
                        <TestCaseHistoryViewer currentTestCase={testCase} />
                        <Center>
                            <SectionCard>
                                <InputOutputRow>
                                    <InputTextField
                                        useForm={false}
                                        label="Input (The most recent user utterance)"
                                        type="text"
                                        value={testCase.text}
                                        placeholder="input text here"
                                        onChange={e => handleChange('text', e.target.value)}
                                        autoFocus
                                    />
                                    <OutputRow>
                                        <OutputTextField
                                            useForm={false}
                                            disabled={true}
                                            label="Output"
                                            type="text"
                                            value={assistantResponse}
                                            placeholder="not simulated yet"
                                            onChange={() => { }}
                                        />

                                        <ResponseJsonButton
                                            disabled={!testCaseContainer?.fullResponses[testCase?.name]?.outputSpeech?.length}
                                            type="button"
                                            themes={['secondary-small', 'end']}
                                            text="Response JSON" onClick={() => setViewResponseJson(true)} />
                                    </OutputRow>

                                </InputOutputRow>
                                <Table>
                                    {(testCase.assertions ?? []).map((c, i) => {
                                        const assertionText = getAssertionText(c)

                                        const variant =
                                            assertionText.toLowerCase() === 'true'
                                                ? 'success'
                                                : assertionText.toLowerCase() === 'false'
                                                    ? 'failure'
                                                    : 'default';

                                        return (
                                            <Row key={i}>
                                                <AssertionPreText>The assistant response should </AssertionPreText>
                                                <AssertionInput
                                                    onChange={(e) => updateAssertion(i, e.currentTarget.value)}
                                                    variant={variant}
                                                    value={c}></AssertionInput>
                                                <AssertionPostText>{getAssertionText(c)}</AssertionPostText>
                                                <RowButton
                                                    type="button"
                                                    themes={['secondary-small', 'end']}
                                                    text="✕"
                                                    onClick={() => removeAssertion(i)}
                                                />
                                            </Row>
                                        )
                                    })}
                                </Table>
                                <AddAssertionButton
                                    type="button"
                                    themes={['start', 'white-pill']}
                                    text="+ Add Assertion"
                                    onClick={addAssertion}
                                />
                            </SectionCard>
                        </Center>
                    </Body>

                </CustomScrollbars>)
                :
                editSessionState ?
                    renderSession()
                    : editUserProfile ?
                        renderUserProfile() :
                        viewResponseJson ?
                            renderResponseJson()
                            : <></>
            }

            <FooterBar>
                {
                    !(editSessionState || editUserProfile || viewResponseJson) ? (
                        <>
                            <Button disabled={false} loading={testCaseContainer.loading} type="button" themes={['secondary', 'start']} text="Simulate" onClick={() => simulate()} />
                            <Button disabled={false} loading={testCaseContainer.loading} type="button" themes={['primary', 'end']} text="Save & Close" onClick={() => handleSave()} />
                        </>)
                        : editSessionState ?
                            <Button type="button" themes={['primary', 'end']} text="Save and go back"
                                onClick={() => { handleChange("sessionState", sessionStateEditorRef.current.getText() as ConversationSessionState); setEditSessionState(false); }} />
                            : editUserProfile ?
                                <Button type="button" themes={['primary', 'end']} text="Save and go back"
                                    onClick={() => { handleChange("userProfile", userStateEditorReft.current.getText() as UserProfile); setEditUserProfile(false); }} />
                                : viewResponseJson ?
                                    <Button type="button" themes={['primary', 'end']} text="Back" onClick={() => setViewResponseJson(false)} />
                                    : <></>}
            </FooterBar>
        </ModalLayout >
    )
};

export default TestCaseEditingModal;

interface BodyProps {
    minHeight?: number;
}
const Body = styled.div<BodyProps>`
    position: relative;
    min-height: ${props => props.minHeight ? `${props.minHeight}px` : 'auto'};
`;

const InputOutputRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;
const OutputRow = styled(InputOutputRow)`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 50%;
`;

const ThinTextField = styled(TextField)`
    height: 32px;
    .field-container {
        height: 32px;
    }
    .field-container input {
        height: 32px;
        padding: 0px 8px;
    }
`


const InputTextField = styled(ThinTextField)`
    margin-right: 8px;
    width: 50%;
`
const OutputTextField = styled(ThinTextField)`
    margin-left: 8px;
`

const AssertionPreText = styled.div`
    font-style: italic;
    font-size: 14px;
    width: 300px;
`;
const AssertionPostText = styled.div`
    font-size: 14px;
    width: 50px;
`;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
`;

const Row = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const RowButton = styled(Button)`
    margin: 0px;
    margin-right: auto;
    margin-left: 30px;
    padding-bottom: 2px;
`;

const HistoryButton = styled(Button)`
    margin: 0px 8px 8px 0px;
    z-index: 10;
`;

const ResponseJsonButton = styled(Button)`
    margin-right: 0px;
    margin-left: 16px;
    top: 15px;
`;

const CustomEditor = styled.div`
    font-family: Muli;
    *.jsoneditor-mode-tree {
        border: 1px solid ${color_shades_dark} ;
        font-family: Muli;
    }
    div.jsoneditor-menu {
        background-color: ${color_shades_darkest};
        border-bottom: none;
    }
    ul.jsoneditor-menu {
        background-color: white;
    }
    *.jsoneditor-navigation-bar {
        font-family: Muli;
    }
    .div.jsoneditor-field, div.jsoneditor-value, div.jsoneditor td, div.jsoneditor th, div.jsoneditor textarea, pre.jsoneditor-preview, .jsoneditor-schema-error, .jsoneditor-popover {
        font-family: Muli;
    }
    div.jsoneditor-tree div.jsoneditor-color {
        display: none;
    }
`;

const LabelWrapper = styled.div`
    display: flex;
    flex-direction: row;
`;

const Label = styled.p`
    font-weight: 400;
    font-size: 16px;
    color: ${color_shades_darkest};
    margin: 0 0 4px 0px;
`;

const SmallLabel = styled.p`
font-weight: 400;
font-size: 14px;
color: ${color_shades_darkest};
margin: 0 0 4px 0px;
`;

const BoldSmallLabel = styled(SmallLabel)`
    font-weight: bold;
    color: ${color_defaults_black};
`;

const InfoLabel = styled.p`
font-weight: 300;
font-size: 14px;
font-style: italic;
color: ${color_shades_darker};
margin: 0 0 4px 0px;
`;

const BoldInfoLabel = styled(InfoLabel)`
    font-weight: bold;
    color: ${color_defaults_black};
`;

const FinalTurnWrapper = styled.div`
`;

const Center = styled.div`
  display: flex;
  justify-content: center;  /* horizontal */
  width:  100%;
`;

const SectionCard = styled.div`
    padding: 16px;
    width: 100%;
    display: block;
    justify-content: flex-start;
`;

const TopRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;
const TopRowButton = styled(Button)`
    margin: 28px 0px 0px 16px;
`;

const HistoryViewer = styled.div`
    /* visuals */
    border-radius: 8px;
    display: block;
    background-color: ${color_shades_lighter};
    height: 250px;
    margin: 0px 16px;
`;

const HistoryViewerInScroll = styled.div`
    padding: 16px 0px;
    margin: 0 16px;
`

const HistoryViewerButtons = styled.div`
    position: absolute;
    top: 120px;
    right: 24px;
    display: flex;
    flex-direction: column;`
    ;

const AddAssertionButton = styled(Button)`
    margin-left: 0px;
    margin-top: 16px;
`;

const bgMap: Record<AssertionVariant, string> = {
    default: "white",
    success: color_variants_green_2,
    failure: color_variants_red_2,
};

interface AssertionInputProps {
    variant?: AssertionVariant;
}
const AssertionInput = styled.input<AssertionInputProps>`
    padding: 8px 12px;
    border: 1px solid ${color_shades_dark};
    border-radius: 6px;
    font-family: Muli;
    font-size: 14px;
    width: 100%;    
    background-color: ${({ variant = 'default' }) => bgMap[variant]};
`;

const DetailedSimpleCheckbox = styled(CheckboxField)`
margin-top: 33px;
margin-left: 8px;
`;


